import { Component, computed, ViewChild, ElementRef, Output, EventEmitter, NgZone, effect, Input, inject, signal, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { GenetricDetailComponent } from '@app/common-codes/components/edit-components/generic-detail.component';
import { Register } from '@app/common-codes/decorators/DependencyInjectionRegistreyDecorators';

import { ReactiveBadgeComponent } from '@app/common-codes/components/nested-components/reactive-badge/reactive-badge.component';
import { ActivatedRoute, Router } from '@angular/router';
import { OnReadyDirective } from '@app/common-codes/components/directives/OnReadyDirective';
import { TranslatePipe } from '@app/common-codes/components/directives/TranslatePipe';
import { AlertModalService } from '@app/common-codes/services/AlertModalService';
import { GenericValidationService } from '@app/common-codes/services/GenericValidationService';
import { LookupItem } from '@app/common-codes/services/LookupService';
import { MatwinsVariablesService } from '@app/common-codes/services/matwinsVariables.service';
import { TranslationService } from '@app/common-codes/services/TranslationService';
import { IStaticStylesConfig } from '@app/common-codes/skins/_primitives/IStaticStylesConfig';
import { MatwinsSkins } from '@app/common-codes/skins/global.skin';
import { Dock } from '@app/common-codes/models/matwins-tenant/Dock.model';
import { Trucker } from '@app/common-codes/models/matwins-tenant/Trucker.model';
import { TruckerActivity } from '@app/common-codes/models/matwins-tenant/TruckerActivity.model';

@Register(TruckerActivity)
@Component({
  selector: 'qr-code-activity-detail',
  templateUrl: './qr-code-activity-detail.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule, TranslatePipe, OnReadyDirective, ReactiveBadgeComponent]
})
export class QrCodeActivityDetailComponent extends GenetricDetailComponent<TruckerActivity> {
  // @Input() override EntityInstance: TruckerActivity | null = null;
  @Input() fkPointOfServiceId: number | null = null;
  @Input() createNewProfile: boolean = false;
  override ComponentEntityClass = TruckerActivity;
  Skin = MatwinsSkins.QrCode1 as IStaticStylesConfig;
  @Output() itemSaved = new EventEmitter<TruckerActivity>();

  // activityStatusLookup = computed(() => this.getLookupData('ActivityStatus'));
  @Input() componentMode: 'checkin' | 'checkout' | 'edit' | undefined = undefined;
  @Input() CanEdit: boolean = true;

  activityStatusLookup = signal<LookupItem[]>([]);
  activityDeliveryTypeLookup = signal<LookupItem[]>([]);
  pointOfServiceLookup = signal<LookupItem[]>([]);
  dockLookup = signal<LookupItem[]>([]);

  filteredDockLookup = computed(() => {
    const dockLookup = this.dockLookup();
    const entity = this.editingEntity();
    const fkPointOfServiceId = entity?.fK_PointOfService;
    if (!dockLookup || !fkPointOfServiceId || !entity) {
      return dockLookup;
    }
    return dockLookup.filter(dock => dock.additionalData['fk_pointofservice'] === fkPointOfServiceId);
  });

  selected: string[] = [];
  selectedCountry = signal<string>('USA'); // Default = USA
  showSCACPortFields = computed(() => this.selectedCountry() === 'USA');

  onStatusChange(newStatus: number): void {
    const currentEntity = this.editingEntity();
    if (currentEntity) {
      const newEntity = Object.assign(new TruckerActivity(), currentEntity);
      newEntity.status = newStatus;
      this.editingEntity.set(newEntity);
    }
  }

  onDeliveryTypeChange(newValue: number): void {
    const currentEntity = this.editingEntity();
    if (currentEntity) {
      const newEntity = Object.assign(new TruckerActivity(), currentEntity);
      newEntity.deliveryType = newValue;
      this.editingEntity.set(newEntity);
    }
  }

  onCountryChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.selectedCountry.set(target.value);

  }

  public override get HasCreatePermission(): boolean { return true; }
  public override get HasEditPermission(): boolean { return this.CanEdit }
  public override get HasUpdatePermission(): boolean { return this.CanEdit }

  public isEmailLocked: boolean = false;
  public isPhoneNumberLocked: boolean = false;

  constructor(
    route: ActivatedRoute,
    router: Router,
    translationService: TranslationService,
    validationService: GenericValidationService,
    alertModalService: AlertModalService,
    matwinsVariables: MatwinsVariablesService,
    ngZone: NgZone
  ) {
    super(route, router, translationService, validationService, ngZone);
    this.alertModalService = alertModalService;

    effect(() => {
      const entity = this.editingEntity();
      if (entity) {
        // Lire la revision : si les listes arrivent en retard, l'effet repasse et les remplit.
        this.lookupService.revision();
        this.activityStatusLookup.set(this.lookupService.getLookupData('ActivityStatus'));
        const activityDeliveryTypeLookup = this.lookupService.getLookupData('ActivityDeliveryType');
        this.activityDeliveryTypeLookup.set(activityDeliveryTypeLookup);
        this.pointOfServiceLookup.set(this.getLookupData('PointOfService'));
        this.dockLookup.set(this.getLookupData('Dock'));

        this.loadPos();
      }
    });


    this.selectedCountry.set('Canada');
  }

  loadPos() {
    const selected = this.editingEntity()?.fK_PointOfService ? [this.editingEntity()!.fK_PointOfService!.toString()] : [];
    this.selected = selected;
  }

  override ngOnInit() {
    super.ngOnInit();
    let trucker = this.editingEntity()?.trucker;
    if (trucker) {
      this.isEmailLocked = (trucker.email?.trim() || '').length > 0;
      this.isPhoneNumberLocked = (trucker.phoneNumber?.trim() || '').length > 0;
    }
  }
  override getEmptyEntity(): TruckerActivity {
    const truckerActivity = new TruckerActivity();
    truckerActivity.status = 1;
    truckerActivity.deliveryType = 1;
    truckerActivity.dock = null; // new Dock();
    truckerActivity.trucker = null;
    truckerActivity.imagePhoto = null;
    truckerActivity.imageAgreements = null; // new FSTREAM_Image();
    truckerActivity.imageSignature = null; // new FSTREAM_Image();
    if (this.fkPointOfServiceId) {
      truckerActivity.fK_PointOfService = this.fkPointOfServiceId;
    }
    return truckerActivity;
  }

  override parseToEntityInstance(): void {
    if (this.EntityInstance && this.editingEntity()) {
      Object.assign(this.EntityInstance, this.editingEntity());
    }
  }

  performSave() {
    const entity = this.editingEntity();
    if (entity) {
      // Create a copy to avoid modifying the original unexpectedly
      const entityToSave = Object.assign(new TruckerActivity(), entity);

      // Envoie la date avec le timezone du navigateur
      if (entityToSave.startDate && !(entityToSave.startDate instanceof Date)) {
        entityToSave.startDate = new Date(entityToSave.startDate);
      }
      // Envoie la date avec le timezone du navigateur (if it exists)
      if (entityToSave.endDate && !(entityToSave.endDate instanceof Date)) {
        entityToSave.endDate = new Date(entityToSave.endDate);
      }

      this.editingEntity.set(entityToSave);

    }
  }

  async saveItem(): Promise<void> {

    // this.performSave();

    const savedItem = await this.save();
    if (savedItem) {
      this.performSavedLookup(savedItem);
      this.itemSaved.emit(savedItem);
    }
  }

  onPOSselectChange(evt: string[]) {
    this.selected = evt;

    const truckerActivity = this.EntityInstance;
    if (!truckerActivity) {
      return;
    }

    const selectedPosIds = (evt ?? [])
      .map(k => Number(k))
      .filter(n => Number.isFinite(n));

    truckerActivity.fK_PointOfService = selectedPosIds[0] ?? null;

  }
  performSavedLookup(savedItem: TruckerActivity) {
    const truckers = this.lookupService.getLookupData('Trucker');
    const trucker = truckers.find(t => t.id === savedItem.fK_Trucker?.toString());
    if (trucker) {
      savedItem.trucker = new Trucker();
      savedItem.trucker.id = Number(trucker.id);
      savedItem.trucker.fullName = trucker.additionalData ? trucker.additionalData['fullname'] : '';
      savedItem.trucker.companyName = trucker.additionalData ? trucker.additionalData['companyname'] : '';
    }

    if (savedItem.fK_Dock) {
      const docks = this.lookupService.getLookupData('Dock');
      const currentDock = docks.find(t => t.id === savedItem.fK_Dock?.toString());
      if (currentDock) {
        savedItem.dock = new Dock();
        savedItem.dock.id = Number(currentDock.id);
        savedItem.dock.iD_SP = currentDock.value;
      }
    }
  }

  async saveAndCloseItem(): Promise<void> {
    // this.performSave();
    const savedItem = await this.save();
    if (savedItem) {

      this.performSavedLookup(savedItem);
      this.itemSaved.emit(savedItem);
      this.closeModal.emit();
    }
  }

  public validateFormAndGetData(withValidation: boolean = true): TruckerActivity | null {
    if (this.validateForm() || !withValidation) {
      return this.editingEntity();
    }
    return null;
  }
}
